/**
 * video-autoplay.js
 *
 * Handles muted / looping / viewport-based autoplay for product videos on:
 *  - Product pages (main gallery + zoom modal)
 *  - Collection pages (product card first media)
 *  - Homepage Featured Product / Featured Collection sections
 *
 * Videos are marked up with the class "js-autoplay-video" (native <video> tags)
 * or "js-autoplay-video js-youtube" / "js-autoplay-video js-vimeo" (iframes).
 *
 * Behaviour:
 *  - A video only plays while it is actually visible in the viewport. As soon as it
 *    scrolls out of view it is paused again, so the browser/device is never doing
 *    decode work for media the visitor can't see (better performance + battery life).
 *  - Any ancestor element can carry a `data-video-autoplay-limit="N"` attribute to
 *    cap how many videos inside it are allowed to play at the same time (used on the
 *    homepage Featured Collection grid so 4 products in a row don't all play at once).
 *    Without that attribute, all currently-visible videos within that group may play.
 */
(function () {
  if (window.Theme && window.Theme.videoAutoplay) return;

  var VISIBILITY_THRESHOLDS = [0, 0.15, 0.25, 0.5, 0.75, 1];
  var PLAY_THRESHOLD = 0.25;

  /** groupEl -> { ratios: Map(video -> ratio), scheduled: Boolean } */
  var groups = new Map();
  var observedVideos = new WeakSet();
  var observedFrames = new WeakSet();

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getGroupElement(el) {
    var group = el.closest('[data-video-autoplay-limit]');
    return group || document.body;
  }

  function getGroupLimit(groupEl) {
    var raw = groupEl.getAttribute && groupEl.getAttribute('data-video-autoplay-limit');
    var parsed = raw ? parseInt(raw, 10) : NaN;
    return isNaN(parsed) || parsed <= 0 ? Infinity : parsed;
  }

  function safePlay(video) {
    if (!video.isConnected) return;
    video.muted = true;
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {
        /* Autoplay can be blocked (e.g. low power mode); fail silently. */
      });
    }
  }

  function safePause(video) {
    if (!video.paused) video.pause();
    try {
      video.currentTime = 0;
    } catch (e) {
      /* Some browsers throw if metadata isn't loaded yet; safe to ignore. */
    }
  }

  function postMessageToFrame(iframe, message) {
    if (!iframe.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(message, '*');
    } catch (e) {
      /* no-op */
    }
  }

  function playFrame(iframe) {
    if (iframe.classList.contains('js-youtube')) {
      postMessageToFrame(iframe, '{"event":"command","func":"playVideo","args":""}');
    } else if (iframe.classList.contains('js-vimeo')) {
      postMessageToFrame(iframe, '{"method":"play"}');
    }
  }

  function pauseFrame(iframe) {
    if (iframe.classList.contains('js-youtube')) {
      postMessageToFrame(iframe, '{"event":"command","func":"pauseVideo","args":""}');
      postMessageToFrame(iframe, '{"event":"command","func":"seekTo","args":[0,true]}');
    } else if (iframe.classList.contains('js-vimeo')) {
      postMessageToFrame(iframe, '{"method":"pause"}');
      postMessageToFrame(iframe, '{"method":"setCurrentTime","value":0}');
    }
  }

  function updateGroup(groupEl) {
    var state = groups.get(groupEl);
    if (!state) return;

    var limit = getGroupLimit(groupEl);
    var visible = [];
    state.ratios.forEach(function (ratio, media) {
      if (ratio >= PLAY_THRESHOLD) visible.push([media, ratio]);
    });
    visible.sort(function (a, b) {
      return b[1] - a[1];
    });

    var allowedToPlay = new Set();
    visible.slice(0, limit).forEach(function (entry) {
      allowedToPlay.add(entry[0]);
    });

    state.ratios.forEach(function (ratio, media) {
      var shouldPlay = ratio >= PLAY_THRESHOLD && allowedToPlay.has(media) && !prefersReducedMotion();
      if (media.tagName === 'VIDEO') {
        shouldPlay ? safePlay(media) : safePause(media);
      } else {
        shouldPlay ? playFrame(media) : pauseFrame(media);
      }
    });
  }

  function observe(media) {
    var groupEl = getGroupElement(media);
    if (!groups.has(groupEl)) {
      groups.set(groupEl, { ratios: new Map() });
    }
    var state = groups.get(groupEl);
    state.ratios.set(media, 0);

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          state.ratios.set(media, entry.isIntersecting ? entry.intersectionRatio : 0);
        });
        updateGroup(groupEl);
      },
      { threshold: VISIBILITY_THRESHOLDS, rootMargin: '200px 0px' }
    );
    observer.observe(media);
  }

  function initVideo(video) {
    if (observedVideos.has(video)) return;
    observedVideos.add(video);
    video.muted = true;
    video.setAttribute('muted', '');
    video.loop = true;
    video.setAttribute('loop', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.removeAttribute('autoplay');
    observe(video);
  }

  function initFrame(iframe) {
    if (observedFrames.has(iframe)) return;
    observedFrames.add(iframe);
    observe(iframe);
  }

  function scan(root) {
    root = root || document;
    if (root.querySelectorAll) {
      root.querySelectorAll('video.js-autoplay-video').forEach(initVideo);
      root.querySelectorAll('iframe.js-autoplay-video').forEach(initFrame);
    }
  }

  // Pick up media that's already in the DOM.
  function start() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Pick up media that gets added later (facets re-render, quick add, section rendering API, etc).
  var mutationObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (
          (node.tagName === 'VIDEO' || node.tagName === 'IFRAME') &&
          node.classList.contains('js-autoplay-video')
        ) {
          node.tagName === 'VIDEO' ? initVideo(node) : initFrame(node);
        } else {
          scan(node);
        }
      });
    });
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

  function refresh() {
    groups.forEach(function (state, groupEl) {
      updateGroup(groupEl);
    });
  }

  window.Theme = window.Theme || {};
  window.Theme.videoAutoplay = { scan: scan, refresh: refresh };
})();
